<template>
  <div class="app-attachment">
    <div class="app-attachment__head">
      <span class="app-attachment__title">
        <i class="fa-duotone fa-paperclip"></i>
        {{ title }}
      </span>
      <span v-if="files && files.length" class="app-attachment__count">
        {{ files.length }} {{ files.length === 1 ? 'file' : 'files' }}
      </span>
    </div>

    <!-- uploaded files -->
    <ul v-if="files && files.length" class="app-attachment__list">
      <li
        v-for="(file, index) in files"
        :key="index"
        class="app-attachment__item"
      >
        <i
          class="app-attachment__file-icon fa-duotone"
          :class="fileMeta(file.name).icon"
          :style="{ '--file-accent': fileMeta(file.name).color }"
        ></i>

        <a
          class="app-attachment__file-name"
          :href="file.uuid_url"
          target="_blank"
          rel="noopener"
          :title="file.name"
        >{{ file.name }}</a>

        <button
          v-if="!disabled"
          type="button"
          class="app-attachment__remove"
          title="Remove attachment"
          @click="$emit('remove', index)"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
      </li>
    </ul>

    <!-- uploader -->
    <div v-if="!disabled" class="app-attachment__uploader">
      <div class="app-attachment__uploader-row">
        <div class="app-attachment__uploader-widget">
          <c-upload
            :id="id"
            :button-text="buttonText"
            data-images-only="false"
            :data-input-accept-types="acceptTypes"
            :data-preferred-types="preferredTypes"
            @onSuccess="(f) => $emit('upload', f)"
          />
        </div>
        <span class="app-attachment__hint">
          <template v-if="hint">{{ hint }}</template>
          <template v-else>Click <strong>“{{ buttonText }}”</strong> to attach a document</template>
        </span>
      </div>

      <ul class="app-attachment__types">
        <li v-for="type in allowedTypes" :key="type">{{ type }}</li>
      </ul>
    </div>

    <p v-else-if="!files || !files.length" class="app-attachment__empty">
      No attachments.
    </p>
  </div>
</template>

<script>
/**
 * Reusable "Attachments" form field: a file list with per-file remove +
 * an Uploadcare-backed uploader and an allowed-types row.
 *
 * Presentational — state lives in the parent. v-model is NOT used; the parent
 * owns the `files` array and reacts to `upload` (raw Uploadcare fileInfo) and
 * `remove` (index). A typical parent maps the upload to `{ name, uuid, uuid_url }`.
 */
export default {
  name: 'AppAttachment',
  props: {
    // Must be unique per uploader instance — Uploadcare binds to this id.
    id: {
      type: String,
      required: true,
    },
    files: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Attachments',
    },
    buttonText: {
      type: String,
      default: 'Choose a file',
    },
    // Plain-text hint; when empty the default "Click ‘…’ to attach a document" shows.
    hint: {
      type: String,
      default: '',
    },
    allowedTypes: {
      type: Array,
      default: () => ['PDF', 'Word', 'Excel', 'Zip', 'JPG/PNG/GIF', 'MP3', 'MP4'],
    },
    acceptTypes: {
      type: String,
      default:
        'application/pdf image/* application/vnd.openxmlformats-officedocument.* audio/* video/* application/zip application/octet-stream text/plain',
    },
    preferredTypes: {
      type: String,
      default:
        'application/pdf image/* application/vnd.openxmlformats-officedocument.* audio/* video/* application/zip',
    },
  },
  emits: ['upload', 'remove'],
  methods: {
    fileMeta(name) {
      const ext = String(name || '').split('.').pop().toLowerCase();

      const map = {
        pdf: { icon: 'fa-file-pdf', color: '#d9534f' },
        doc: { icon: 'fa-file-word', color: '#337ab7' },
        docx: { icon: 'fa-file-word', color: '#337ab7' },
        xls: { icon: 'fa-file-excel', color: '#3f9e5a' },
        xlsx: { icon: 'fa-file-excel', color: '#3f9e5a' },
        csv: { icon: 'fa-file-excel', color: '#3f9e5a' },
        zip: { icon: 'fa-file-zipper', color: '#e0a800' },
        jpg: { icon: 'fa-file-image', color: '#7b3ac9' },
        jpeg: { icon: 'fa-file-image', color: '#7b3ac9' },
        png: { icon: 'fa-file-image', color: '#7b3ac9' },
        gif: { icon: 'fa-file-image', color: '#7b3ac9' },
        webp: { icon: 'fa-file-image', color: '#7b3ac9' },
        mp3: { icon: 'fa-file-audio', color: '#14a3a3' },
        wav: { icon: 'fa-file-audio', color: '#14a3a3' },
        mp4: { icon: 'fa-file-video', color: '#5145cd' },
        mov: { icon: 'fa-file-video', color: '#5145cd' },
      };

      return map[ext] || { icon: 'fa-file', color: '#6b7280' };
    },
  },
};
</script>

<style lang="scss" scoped>
.app-attachment {
  margin-top: 20px;
  margin-bottom: 22px;
  padding-top: 18px;
  border-top: 1px solid #e6e9ef;

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #2f3947;

    i {
      color: #337ab7;
      margin-right: 4px;
    }
  }

  &__count {
    font-size: 12px;
    font-weight: 600;
    color: #5f6b7a;
    background: #eef2f7;
    border-radius: 20px;
    padding: 2px 10px;
  }

  &__list {
    list-style: none;
    margin: 0 0 14px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 9px 12px;
    background: #fff;
    border: 1px solid #e6e9ef;
    border-radius: 8px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: #cbd5e1;
      box-shadow: 0 1px 4px rgba(15, 34, 58, 0.06);
    }
  }

  &__file-icon {
    font-size: 20px;
    color: var(--file-accent, #6b7280);
    flex: 0 0 auto;
  }

  &__file-name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
    font-weight: 500;
    color: #2f3947;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      color: #337ab7;
      text-decoration: underline;
    }
  }

  &__remove {
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #98a2b3;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      background: #fdecec;
      color: #d9534f;
    }
  }

  &__uploader {
    background: #f7f9fc;
    border: 1px solid #e6e9ef;
    border-radius: 8px;
    padding: 14px 16px;
  }

  &__uploader-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__uploader-widget {
    flex: 0 0 auto;
  }

  &__hint {
    font-size: 12px;
    color: #6b7688;

    strong {
      font-weight: 600;
      color: #2f3947;
    }
  }

  &__types {
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    li {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.02em;
      color: #6b7688;
      background: #edf1f6;
      border-radius: 4px;
      padding: 2px 8px;
    }
  }

  &__empty {
    margin: 0;
    font-size: 13px;
    color: #98a2b3;
    font-style: italic;
  }
}
</style>

<style lang="scss">
/* Uploadcare widget lives in dynamically-injected DOM, so target it outside scoped styles
   but confine the override to this component via the wrapper class. */
.app-attachment .app-attachment__uploader-widget {
  .c-upload-widget {
    margin-bottom: 0;
  }

  /* c-upload prints the selected file name in a bare <span> under the button;
     the file already shows in the list above, and it shifts the hint — hide it. */
  .c-upload-widget > span {
    display: none;
  }

  .uploadcare--widget__button_type_open {
    background: #337ab7;
    border-radius: 6px;
    font-weight: 600;
    padding: 7px 16px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #2c6ba0;
    }
  }

  .uploadcare--widget__text {
    color: #6b7688;
    font-size: 12px;
  }
}
</style>
